import * as React from 'react';
import { withRouter, RouteComponentProps, Link } from 'react-router-dom';
import * as moment from 'moment';
import { Button, Card, Col, Divider, Modal, notification, Row, Table, Input, Space, DatePicker } from 'antd';
import { CheckOutlined, CloseOutlined, FilterOutlined, ReloadOutlined, CloseCircleOutlined, ExportOutlined, ImportOutlined } from '@ant-design/icons';
import { Title } from '../../components/Layout/components';
import CustomersQuery, { CustomerFilter } from '../../components/Query/Customers/Customers';
import CurrenciesQuery from '../../components/Query/Currencies/Currencies';
import NotFoundPage from '../NotFound/NotFound';
import CustomerDetail from './CustomerDetail';
import CustomerDiscountImport from './CustomerDiscountImport';
import PriceDisplay from '../../components/Others/PriceDisplay';
import { callAuthorizeBackend } from 'src/helpers/Backend/callAuthorizeBackend';
import CustomerGroup from 'src/components/Query/CustomerGroup/CustomerGroup';

interface ICustomersTableState {
  filterParams: CustomerFilter;
  filter: CustomerFilter | null;
  limit: number;
  orderBy?: string;
  customer?: GQL.ICustomer;
  discountImportVisible?: boolean;
}

interface ICustomersTableProps extends RouteComponentProps {
  searchQuery: string;
  domain: string;
  customerId?: string;
  filter: any;
  goToUrl: (url: string) => void;
  onSearch: (params: any) => void;
}

class CustomersTable extends React.Component<ICustomersTableProps, ICustomersTableState> {
  private timeout;

  constructor(props: any) {
    super(props);

    this.state = {
      filterParams: {
        ...props.filter
      } as any,
      filter: null,
      limit: 40,
    };

    this.setFilterParam = this.setFilterParam.bind(this);
    this.setFilter = this.setFilter.bind(this);
  }

  public render() {
    return (
      <Row>
        <Col span={24}>
          <CustomersQuery
            filter={this.state.filterParams}
            domain={this.props.domain}
            {...(this.props.customerId
              ? { customerId: this.props.customerId }
              : { limit: this.state.limit, skip: (this.getPage() - 1) * this.state.limit, orderBy: this.state.orderBy })}
          >
            {({ customers, updateCustomer, deleteCustomer }: any) => {
              return this.renderData(customers, updateCustomer, deleteCustomer, customers.refetch);
            }}
          </CustomersQuery>
        </Col>
      </Row>
    );
  }

  private setFilterParam(name: string, value: any) {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    const page = name === 'page' ? value : 1;
    this.timeout = setTimeout(() => {
      this.setState({
        filterParams: { ...this.state.filterParams, [name]: value, page }
      });

      this.setFilter();
    }, 500);
  };

  private setFilter() {
    this.props.onSearch({ ...this.state.filterParams });
  };

  private getPage() {
    return parseInt(String(this.state.filterParams.page || 1), 10) || 1;
  }

  private getSortOrder(field: string) {
    const [sortField, order] = (this.state.orderBy || '').split('_');
    return sortField === field ? (order === 'ASC' ? 'ascend' : 'descend') : null;
  }

  private onTableChange(pagination: any, filters: any, sorter: any) {
    const orderBy = sorter && sorter.order ? `${sorter.columnKey}_${sorter.order === 'ascend' ? 'ASC' : 'DESC'}` : undefined;
    const customerGroup = filters.group || null;
    const receiveNewsletter = filters.receiveNewsletter || null;
    const changed = orderBy !== this.state.orderBy
      || JSON.stringify(customerGroup) !== JSON.stringify(this.state.filterParams.customerGroup || null)
      || JSON.stringify(receiveNewsletter) !== JSON.stringify(this.state.filterParams.receiveNewsletter || null);

    this.setState({
      filterParams: {
        ...this.state.filterParams,
        customerGroup,
        receiveNewsletter,
        page: changed ? 1 : pagination.current,
      },
      limit: pagination.pageSize || this.state.limit,
      orderBy,
    }, () => this.setFilter());
  }

  private handleReset(clearFilters) {
    clearFilters();
  };

  private resetAllFilters() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.timeout = setTimeout(() => {
      this.setState({
        filterParams: {
          query: undefined,
          page: 1,
          createdAtFrom: null,
          createdAtTo: null,
          customerGroup: null,
          discount: null,
          vouchersUsedFrom: null,
          vouchersUsedTo: null,
          discountFrom: null,
          discountTo: null,
          orderCountFrom: null,
          orderCountTo: null,
          currencies: null,
          grandTotalFrom: null,
          grandTotalTo: null,
          lastOrderDateFrom: null,
          lastOrderDateTo: null,
          receiveNewsletter: null,
          lastLoginFrom: null,
          lastLoginTo: null,
        }
      });

      this.setFilter();
    }, 500);
  };

  private renderFilterForm(refetch: Function) {
    const inputStyle = { marginRight: '4px', marginBottom: '4px' };

    return (
      <React.Fragment>
        <Row>
          <Col span={24}>
            <Input
              placeholder="Search (email, phone, customer name, street, zip, city, country)"
              defaultValue={this.state.filterParams.query}
              onChange={(e: any) => this.setFilterParam('query', e.target.value)}
              style={{ ...inputStyle, maxWidth: '900px' }}
            />
            <Button onClick={this.setFilter} style={inputStyle}>
              <FilterOutlined />
              Filter
            </Button>
            <Button type="primary" onClick={(e: any) => refetch()} style={inputStyle}>
              <ReloadOutlined />
              Refresh
            </Button>
            <Button onClick={(e: any) => this.resetAllFilters()} style={inputStyle}>
              <CloseCircleOutlined />
              Reset all filters
            </Button>
            <Button onClick={(e: any) => this.downloadExport()} style={inputStyle}>
              <ExportOutlined />
              Download customers
            </Button>
            <Button onClick={() => this.setState({ discountImportVisible: true })} style={inputStyle}>
              <ImportOutlined />
              Import discounts
            </Button>
          </Col>
        </Row>
        {this.state.discountImportVisible &&
          <CustomerDiscountImport
            onClose={() => this.setState({ discountImportVisible: false })}
            onImported={() => {
              refetch();
              this.setState({ discountImportVisible: false });
            }}
          />}
        <Divider />
      </React.Fragment>
    );
  }

  private renderData(customers: any, updateCustomer, deleteCustomer, refetch: Function) {
    const { domain } = this.props;

    if (customers && customers.customers) {
      // formulář
      if (this.props && this.props.customerId) {
        const selectedCustomer = customers.customers.find((s: GQL.ICustomer) => s.id === this.props.customerId) || undefined;
        if (this.props.customerId === 'new' || !selectedCustomer) {
          return <NotFoundPage domain={this.props.domain} />
        }
        return (
          <CustomerDetail
            domain={this.props.domain}
            customer={selectedCustomer || {}}
            onSave={a => this.upsert(a, updateCustomer, refetch)}
            onCancel={() => this.cancel()}
          />
        );
      }
      const inputStyle = { marginRight: '4px', marginBottom: '4px' };

      return (
        <CurrenciesQuery>
          {({ primaryCurrency }) =>
            <React.Fragment>
              <Title>Customers</Title>
              <Divider />

              {this.renderFilterForm(refetch)}

              <CustomerGroup>
              {({ customerGroupList }: any) =>
              <Table
                rowKey={(record: GQL.ICustomer) => record.id}
                dataSource={customers.customers || []}
                loading={customers.loading}
                pagination={{
                  current: this.getPage(),
                  pageSize: this.state.limit,
                  total: customers.customersCount || 0,
                  showTotal: total => `${total} customers`,
                }}
                onChange={(pagination, filters, sorter) => this.onTableChange(pagination, filters, sorter)}
                defaultExpandAllRows={true}
                style={{
                  marginTop: '20px',
                }}
              >
                <Table.Column
                  render={(u, row: GQL.ICustomer) => {
                    return (
                      <>
                        <span style={{ color: row.inactive ? 'red' : 'black', fontWeight: row.inactive ? 'bold' : 'normal' }}>
                          {moment(new Date(row.createdAt))
                            .local()
                            .format('DD/MM/YY')}
                        </span>
                      </>
                    );
                  }}
                  title="Registered"
                  dataIndex="createdAt"
                  key="createdAt"
                  sorter={true}
                  sortOrder={this.getSortOrder('createdAt')}
                  filterDropdown={({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                    <div style={{ padding: 8 }}>
                      <Space>
                        <DatePicker
                          style={{ width: 135, marginBottom: 8 }}
                          onChange={e => { e && this.setFilterParam('createdAtFrom', e.startOf('day').unix().toString()) }}
                          value={this.state.filterParams.createdAtFrom && moment.unix(parseInt(this.state.filterParams.createdAtFrom)) || null}
                          placeholder={'From date'}
                          allowClear={false}
                        />
                        <DatePicker
                          style={{ width: 135, marginBottom: 8 }}
                          onChange={e => { e && this.setFilterParam('createdAtTo', e.startOf('day').unix().toString()) }}
                          value={this.state.filterParams.createdAtTo && moment.unix(parseInt(this.state.filterParams.createdAtTo)) || null}
                          placeholder={'To date'}
                          allowClear={false}
                        />
                      </Space>
                      <br />
                      <Space>
                        <Button
                          onClick={() => {
                            this.handleReset(clearFilters);
                            this.setState({
                              filterParams: {
                                ...this.state.filterParams,
                                createdAtFrom: null,
                                createdAtTo: null
                              }
                            });
                          }}
                          style={{ width: 278 }}
                        >
                          Reset
                        </Button>
                      </Space>
                    </div>
                  }
                  filteredValue={(this.state.filterParams.createdAtFrom || this.state.filterParams.createdAtTo) ? ['active'] : null}
                  width='7%'
                />
                <Table.Column
                  render={(u, row: GQL.ICustomer) => {
                    return (
                      <span>
                        {row.invoiceFirstName} {row.invoiceLastName}
                      </span>
                    );
                  }}
                  title="Name"
                  dataIndex="name"
                  key="name"
                  sorter={true}
                  sortOrder={this.getSortOrder('name')}
                  width='10%'
                />
                <Table.Column
                  render={(u, row: GQL.ICustomer) => {
                    return <span>{row.email} </span>;
                  }}
                  title="Email"
                  dataIndex="email"
                  key="email"
                  sorter={true}
                  sortOrder={this.getSortOrder('email')}
                  width='15%'
                />
                <Table.Column
                  title="Country"
                  dataIndex="invoiceCountry"
                  key="invoiceCountry"
                  render={(u, row: GQL.ICustomer) => {
                    return <span>{row.invoiceCountry}</span>;
                  }}
                  sorter={true}
                  sortOrder={this.getSortOrder('invoiceCountry')}
                  width='7%'
                />
                <Table.Column
                  title="Group"
                  dataIndex="group"
                  key="group"
                  render={(u, row: GQL.ICustomer) => {
                    return <span>{row.customerGroup ? row.customerGroup.name : ''}</span>;
                  }}
                  sorter={true}
                  sortOrder={this.getSortOrder('group')}
                  filters={(customerGroupList && customerGroupList.customerGroupList || [])
                    .map((group: GQL.ICustomerGroup) => group.name)
                    .filter((name: string, i: number, names: string[]) => name && names.indexOf(name) === i)
                    .sort()
                    .map((name: string) => ({ text: name, value: name }))}
                  filteredValue={
                    this.state.filterParams.customerGroup
                    && (Array.isArray(this.state.filterParams.customerGroup)
                      ? this.state.filterParams.customerGroup
                      : [this.state.filterParams.customerGroup])
                    || null
                  }
                  width='5%'
                />
                <Table.Column
                  title="Groups"
                  dataIndex="groups"
                  key="groups"
                  width='10%'
                  render={(u, row: GQL.ICustomer) =>
                    <CustomerGroup>
                      {({ customerGroupList, loading }: any,) => {
                        if (!row.customerGroupIds || loading === true || !Array.isArray(customerGroupList.customerGroupList)) { return null }
                        return row.customerGroupIds.map((groupId) => {
                          const group = customerGroupList.customerGroupList.find((g: GQL.ICustomerGroup) => g.id === groupId);
                          if (group && group.name) {
                            return <React.Fragment>{group.name}<br/></React.Fragment>;
                          }
                          return null;
                        });
                      }}
                    </CustomerGroup>
                    // Question? podle čeho řadit, zatím to neřadí a nefiltruje, obojí je v předcházejícím sloupci
                  }
                />
                <Table.Column
                  title="Discount"
                  dataIndex="discount"
                  key="discount"
                  align="center"
                  render={(u, row: GQL.ICustomer) => {
                    if (!row.discount) {
                      return null;
                    }

                    return `${row.discount} %`;
                  }}
                  sorter={true}
                  sortOrder={this.getSortOrder('discount')}
                  width='6%'
                  filterDropdown={({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                    <div style={{ padding: 8 }}>
                      <Space>
                        <Input
                          style={{ width: 90, marginBottom: 8 }}
                          type={'number'}
                          onChange={e => { e.target.value && this.setFilterParam('discountFrom', e.target.value) }}
                          defaultValue={this.state.filterParams.discountFrom || ''}
                          placeholder={'From'}
                        />
                        <Input
                          style={{ width: 90, marginBottom: 8 }}
                          type={'number'}
                          onChange={e => { e.target.value && this.setFilterParam('discountTo', e.target.value) }}
                          defaultValue={this.state.filterParams.discountTo || ''}
                          placeholder={'To'}
                        />
                      </Space>
                      <br />
                      <Space>
                        <Button
                          onClick={() => {
                            this.handleReset(clearFilters);
                            this.setState({
                              filterParams: {
                                ...this.state.filterParams,
                                discountFrom: null,
                                discountTo: null
                              }
                            });
                          }}
                          style={{ width: 188 }}
                        >
                          Reset
                        </Button>
                      </Space>
                    </div>

                  }
                  filteredValue={(this.state.filterParams.discountFrom || this.state.filterParams.discountTo) ? ['active'] : null}
                />

                <Table.Column
                  title="News."
                  dataIndex="receiveNewsletter"
                  key="receiveNewsletter"
                  width='5%'
                  align="center"
                  render={(u, row: GQL.ICustomer) => {
                    return (
                      <span>
                        {row.receiveNewsletter
                          ? <CheckOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                          : <CloseOutlined style={{ fontSize: 20 }} />}
                      </span>
                    );
                  }}
                  sorter={true}
                  sortOrder={this.getSortOrder('receiveNewsletter')}
                  filters={[
                    {
                      text: 'Yes',
                      value: 'true'
                    },
                    {
                      text: 'No',
                      value: 'false'
                    }
                  ]}
                  filteredValue={
                    this.state.filterParams.receiveNewsletter
                    && (Array.isArray(this.state.filterParams.receiveNewsletter)
                      ? this.state.filterParams.receiveNewsletter
                      : [this.state.filterParams.receiveNewsletter])
                    || null
                  }
                />
                <Table.Column
                  title="Last login"
                  dataIndex="lastLogin"
                  key="lastLogin"
                  width='8%'
                  render={(u, row: GQL.ICustomer) => {
                    const lastLogin = row.lastLogin;

                    return (
                      <React.Fragment>
                        {lastLogin &&
                          <React.Fragment>
                            {moment(new Date(lastLogin))
                              .local()
                              .format('DD/MM/YY HH:mm')}
                          </React.Fragment>}
                      </React.Fragment>
                    );
                  }}
                  sorter={true}
                  sortOrder={this.getSortOrder('lastLogin')}
                  filterDropdown={({ setSelectedKeys, selectedKeys, confirm, clearFilters }) =>
                    <div style={{ padding: 8 }}>
                      <Space>
                        <DatePicker
                          style={{ width: 135, marginBottom: 8 }}
                          onChange={e => { e && this.setFilterParam('lastLoginFrom', e.startOf('day').unix().toString()) }}
                          value={this.state.filterParams.lastLoginFrom && moment.unix(parseInt(this.state.filterParams.lastLoginFrom)) || null}
                          placeholder={'From date'}
                          allowClear={false}
                        />
                        <DatePicker
                          style={{ width: 135, marginBottom: 8 }}
                          onChange={e => { e && this.setFilterParam('lastLoginTo', e.startOf('day').unix().toString()) }}
                          value={this.state.filterParams.lastLoginTo && moment.unix(parseInt(this.state.filterParams.lastLoginTo)) || null}
                          placeholder={'To date'}
                          allowClear={false}
                        />
                      </Space>
                      <br />
                      <Space>
                        <Button
                          onClick={() => {
                            this.handleReset(clearFilters);
                            this.setState({
                              filterParams: {
                                ...this.state.filterParams,
                                lastLoginFrom: null,
                                lastLoginTo: null
                              }
                            });
                          }}
                          style={{ width: 278 }}
                        >
                          Reset
                        </Button>
                      </Space>
                    </div>
                  }
                  filteredValue={(this.state.filterParams.lastLoginFrom || this.state.filterParams.lastLoginTo) ? ['active'] : null}
                />
                <Table.Column title="Pohoda ID" render={(row: any) => row.pohodaCustomerId}/>
                <Table.Column
                  title="Actions"
                  dataIndex="actions"
                  key="actions"
                  align="center"
                  render={(u: any, row: any) => this.renderActions(u, row, deleteCustomer, customers.refetch)}
                />
              </Table>}
              </CustomerGroup>
            </React.Fragment>
          }
        </CurrenciesQuery>
      );
    }

    return (
      <div>
        <Divider />
        <Card loading={true} title="Loading customers ..." style={{ width: '100%' }} />
      </div>
    );
  }

  private renderActions(u: any, customer: GQL.ICustomer, deleteCategory: Function, refetch: Function) {
    return (
      <div>
        <Link className="ant-btn" type={'dashed'} to={this.getLink(customer.id)}>
          Edit
        </Link>
        <Divider type={'vertical'} />
        <Button disabled={false} type={'dashed'} onClick={() => this.goToOrders(customer.id)}>
          Orders
        </Button>
      </div>
    );
  }

  private goToOrders(id: string) {
    this.props.goToUrl(`/${this.props.domain}/application/orders/${id}`);
  };

  private delete(customer: any, deleteCustomer: Function, refetch: Function) {
    Modal.confirm({
      title: `Delete ${customer.title}?`,
      content: (
        <span>
          Are you sure you want to delete customer <strong>{customer.title}</strong>
        </span>
      ),
      onOk: () => {
        return deleteCustomer({
          variables: {
            id: customer.id
          }
        }).then(() => refetch());
      }
    });
  }

  private downloadExport() {
    // volání backendu
    callAuthorizeBackend({
      path: '/customers/export',
      // data: {
      //   currency: currency && currency.value,
      // },
      method: 'POST',
      executeDownload: true,
      data: { args: { where: { filter: this.state.filterParams } } },
    });
  }

  private upsert(
    customer: GQL.ICustomer,
    // createCustomer: Function,
    updateCustomer: Function,
    refetch: Function
  ) {
    if (customer !== undefined) {
      let customerGroupIds: string[] = [];
      if (Array.isArray(customer.customerGroups)) {
        customerGroupIds = Array.isArray(customer.customerGroups) ? customer.customerGroups.map((group) => { return group.id }) : [];
      }
      const variables = {
        id: customer.id,
        customer: {
          email: customer.email,
          phone: customer.phone,

          receiveNewsletter: customer.receiveNewsletter,

          invoiceFirstName: customer.invoiceFirstName,
          invoiceLastName: customer.invoiceLastName,
          invoiceStreet: customer.invoiceStreet,
          invoiceHouseNumber: customer.invoiceHouseNumber,
          invoiceAddressLine1: customer.invoiceAddressLine1,
          invoiceAddressLine2: customer.invoiceAddressLine2,
          invoiceZip: customer.invoiceZip,
          invoiceCity: customer.invoiceCity,
          invoiceCountyState: customer.invoiceCountyState,
          invoiceCountry: customer.invoiceCountry,

          companyInformation: customer.companyInformation,
          companyName: customer.companyName,
          companyVatNumber: customer.companyVatNumber,
          companyRegistrationNumber: customer.companyRegistrationNumber,

          anotherDeliveryAddress: customer.anotherDeliveryAddress,
          deliveryStreet: customer.deliveryStreet,
          deliveryHouseNumber: customer.deliveryHouseNumber,
          deliveryAddressLine1: customer.deliveryAddressLine1,
          deliveryAddressLine2: customer.deliveryAddressLine2,
          deliveryZip: customer.deliveryZip,
          deliveryCity: customer.deliveryCity,
          deliveryCountyState: customer.deliveryCountyState,
          deliveryCountry: customer.deliveryCountry,

          customerGroupId: customer.customerGroup ? customer.customerGroup.id : null,
          customerGroupIds: customerGroupIds, // don't use customer.customerGroupIds here, as only customer.customerGroups are modified in the edit form
          
          discount: customer.discount,

          saleTaxCertificate: customer.saleTaxCertificate,
          inactive: customer.inactive || false, // default to false if not set
          pohodaCustomerId: customer.pohodaCustomerId,
        }
      };

      if (customer.id) {
        return updateCustomer({ variables })
          .then(() => refetch())
          .then(() => this.cancel())
          .then(() =>
            notification['success']({
              message: 'Info',
              description: `Customer "${variables.customer.invoiceLastName} ${variables.customer.invoiceFirstName}" has been updated.`
            })
          );
      }

      // (variables.customer as any).domain = this.props.domain;

      // createCustomer({ variables })
      //   .then(() => refetch())
      //   .then(() => this.cancel())
      //   .then(() =>
      //     notification['success']({
      //       message: 'Info',
      //       description: `Payment method "${variables.customer.title}" has been created.`
      //     })
      //   );
    }
  }

  private getLink(recordId: string | null) {
    return `/${this.props.domain}/application/customers${recordId ? `/${recordId}` : ''}`;
  }

  private cancel() {
    this.props.history.push(this.getLink(null));
  }
}

export default withRouter(CustomersTable);



// WEBPACK FOOTER //
// ./src/scenes/Customers/CustomersTable.tsx